import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, User, PuzzleIcon } from "lucide-react";
import Header from "@/components/header";
import { useTranslation } from "@/i18n/LanguageContext";
import { useTitle } from "@/hooks/use-title";
import { Link } from "wouter";

interface RankedUser {
  id: number;
  displayName: string;
  avatarUrl: string;
  solvedCount: number;
  totalEarned: number;
  cacataisPerPuzzle: number;
}

interface RankedPuzzle {
  id: number;
  title: string;
  creatorName: string;
  creatorAvatarUrl: string | null;
  prize: number;
  solvedByCount: number;
  averageRating: number;
  totalRatings: number;
}

const formatNumber = new Intl.NumberFormat().format;

export default function PuzzlesRankPage() {
  const { t } = useTranslation();
  useTitle(t("rank.page_title"));
  const [sortBy, setSortBy] = useState<string>("solvedCount");
  
  // Fetch users who solved the most custom puzzles
  const { data: topSolvers, isLoading: isLoadingTopSolvers } = useQuery<RankedUser[]>({
    queryKey: ["puzzles-top-solvers", sortBy],
    queryFn: async () => {
      const response = await fetch(`/api/puzzles/custom/top-solvers?sortBy=${sortBy}`);
      if (!response.ok) {
        throw new Error("Failed to fetch top solvers");
      }
      return response.json();
    }
  });

  // Fetch most popular custom puzzles
  const { data: topPuzzles } = useQuery<RankedPuzzle[]>({
    queryKey: ["puzzles-top-puzzles"],
    queryFn: async () => {
      const response = await fetch("/api/puzzles/custom/top-puzzles");
      if (!response.ok) {
        throw new Error("Failed to fetch top puzzles");
      }
      return response.json();
    }
  });

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6 space-y-6 pb-12">
        <h1 className="text-3xl font-bold text-white mb-6">{t("rank.page_title")}</h1>
        
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="h-6 w-6 text-yellow-500" />
                {t("rank.top_solvers")}
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">{t("rank.sort_by")||"Sort by:"}</span>
                <Select
                  value={sortBy}
                  onValueChange={setSortBy}
                >
                  <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder={t("rank.sort_placeholder")||"Select sorting"} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="solvedCount">{t("rank.solved_puzzles")}</SelectItem>
                    <SelectItem value="totalEarned">{t("rank.total_earned")}</SelectItem>
                    <SelectItem value="cacataisPerPuzzle">{t("rank.cacatais_per_puzzle")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{t("rank.ranking_position")}</TableHead>
                  <TableHead>{t("rank.user")}</TableHead>
                  <TableHead className="text-right">{t("rank.solved_puzzles")}</TableHead>
                  <TableHead className="text-right">{t("rank.total_earned")}</TableHead>
                  <TableHead className="text-right">{t("rank.cacatais_per_puzzle") || "Cacatais/Desafio"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingTopSolvers ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        <span className="ml-2 text-gray-400">{t("rank.loading")||"Loading..."}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : topSolvers?.map((user, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatarUrl || undefined} />
                          <AvatarFallback>
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <Link href={`/users/${user.displayName ? user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : user.id}`} className="hover:underline hover:text-blue-400 transition-colors">
                          {user.displayName || 'Anonymous'}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(user.solvedCount)}</TableCell>
                    <TableCell className="text-right">{formatNumber(user.totalEarned)} {t("rank.cacatais")}</TableCell>
                    <TableCell className="text-right">{user.solvedCount > 0 ? (user.totalEarned / user.solvedCount).toFixed(2) : 0}</TableCell>
                  </TableRow>
                ))}
                {!isLoadingTopSolvers && !topSolvers?.length && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-gray-400">
                      {t("rank.no_data")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PuzzleIcon className="h-6 w-6 text-blue-500" />
              {t("rank.most_popular_puzzles")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{t("rank.ranking_position")}</TableHead>
                  <TableHead>{t("rank.puzzle")}</TableHead>
                  <TableHead>{t("rank.creator")}</TableHead>
                  <TableHead className="text-right">{t("rank.review")}</TableHead>
                  <TableHead className="text-right">{t("rank.prize")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topPuzzles?.map((puzzle, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <a 
                        href={`/puzzles/custom/${puzzle.id}`} 
                        className="hover:text-blue-400 transition-colors"
                      >
                        {puzzle.title}
                      </a>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={puzzle.creatorAvatarUrl || undefined} />
                          <AvatarFallback>
                            <User className="h-3 w-3" />
                          </AvatarFallback>
                        </Avatar>
                        <Link href={`/users/${puzzle.creatorName ? puzzle.creatorName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : ''}`} className="hover:underline hover:text-blue-400 transition-colors">
                          {puzzle.creatorName || 'Anonymous'}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {puzzle.totalRatings > 0 ? (
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-yellow-500">★</span>
                          <span>{puzzle.averageRating.toFixed(1)}</span>
                          <span className="text-gray-400 text-xs">({puzzle.totalRatings})</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">{t("rank.no_reviews")}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(puzzle.prize)}</TableCell>
                  </TableRow>
                ))}
                {!topPuzzles?.length && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-gray-400">
                      {t("rank.no_data")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
